import { DocsLayout } from '@/business-components/docs-layout/DocsLayout';
import { ErrorContent } from '@/business-components/error-content/ErrorContent';
import { GlobalComponent } from '@/business-components/global-component/GlobalComponent';
import { GlobalScript } from '@/business-components/global-script/GlobalScript';
import { RouteError } from '@/business-components/route-error/RouteError';
import { DocsSiteLoader } from '@/domains/docs-site/DocsSiteLoader';
import { prepareServerContext } from '@/domains/global/prepareServerContext';
import { RequestContext } from '@/domains/global/RequestContext';
import { requestInterceptor } from '@/domains/request-interceptor/RequestInterceptor';
import { useClientCacheLoaderData } from '@/hooks/useClientCacheLoaderData';
import { GlobalModelProvider } from '@/models/global/GlobalModelProvider';
import { AppTheme } from '@/shared/constant/AppTheme';
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { data as remixRunData } from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from '@remix-run/react';
import type React from 'react';
import './root.css';

export async function loader(loaderArgs: LoaderFunctionArgs) {
  const interceptedRequest = requestInterceptor.intercept(loaderArgs.request);
  if (interceptedRequest) {
    loaderArgs.context.interceptedRequest = interceptedRequest;

    throw new Error('Intercepted request');
  }

  const requestData = prepareServerContext(loaderArgs.request);
  const requestContext = new RequestContext({
    requestData,
  });
  Object.assign(loaderArgs.context, {
    requestContext,
  });

  const data = await new DocsSiteLoader(loaderArgs).load();
  return remixRunData(data, {
    headers: requestContext.getResponseHeaders(),
    status: requestContext.getResponseStatusCode(),
  });
}

export const headers: HeadersFunction = (loaderArgs) => {
  return loaderArgs.loaderHeaders;
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [...(data?.meta ?? [])];
};

export default function App() {
  return <Outlet />;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const loaderOriginData = useLoaderData<typeof loader | undefined>();
  const loaderData = useClientCacheLoaderData(loaderOriginData);
  const error = useRouteError();

  return (
    <html
      lang={
        loaderData?.docsDataState?.docsBaseConfig?.language ?? loaderData?.envConfig?.DEFAULT_LOCALE
      }
      className={`appui-theme-${AppTheme.Light} body-theme-primary-purple group/root`}
      id="html"
      suppressHydrationWarning
    >
      <head suppressHydrationWarning>
        <GlobalScript
          docsBaseConfig={loaderData?.docsDataState?.docsBaseConfig}
          docsIdTypeData={loaderData?.docsDataState?.docsIdTypeData}
        />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="overflow-hidden" suppressHydrationWarning>
        <GlobalModelProvider
          i18nState={loaderData?.i18nState}
          docsDataState={loaderData?.docsDataState}
          clientConfig={loaderData?.clientConfig}
          urlConfig={loaderData?.urlConfig}
          envConfig={loaderData?.envConfig}
        >
          {loaderOriginData && (
            <GlobalComponent>
              {!loaderOriginData.errorCodeMessage && <DocsLayout />}

              {loaderOriginData.errorCodeMessage && (
                <ErrorContent errorCodeMessage={loaderOriginData.errorCodeMessage} />
              )}
            </GlobalComponent>
          )}
          {!loaderOriginData && (
            <>
              {error && <RouteError error={error} />}
              {!error && children}
            </>
          )}
        </GlobalModelProvider>

        <script
          dangerouslySetInnerHTML={{
            __html: `
            if (typeof window.__updateThemeElement === 'function') {
              window.__updateThemeElement();
            }
          `,
          }}
        />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}
